import React, { useState, useRef } from "react"; // Import useRef
import styles from "./FeedItemPreview.module.css";
import PropTypes from "prop-types";
import FeedItem from "../FeedItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDesktop,
  faImage,
  faMobileAlt,
  faTextHeight,
} from "@fortawesome/free-solid-svg-icons";
import { DisplaySectionContent } from "../DisplaySectionContent/DisplaySectionContent";
import BannerItem from "../BannerItem";
import FeedItemModal from "../FeedItemModal";
import { CSSTransition } from "react-transition-group";

/**
 * Interactive component. Displays the content of a feed item in different environments.
 */

export function FeedItemPreview({ feedItem }) {
  const [isMobile, setIsMobile] = useState(true);
  const [isFeedItemVisible, setIsFeedItemVisible] = useState(true);
  const [isDisplayVisible, setIsDisplayVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const modalRef = useRef(null); // Instatiate ref

  const handleMobileClick = () => {
    setIsMobile(true);
  };

  const handleDesktopClick = () => {
    setIsMobile(false);
  };

  // Function to hide card when image button clicked.
  const handleImageButtonClick = () => {
    setIsFeedItemVisible(!isFeedItemVisible);
  };

  // Function to display content when text button clicked.
  const handleTextButtonClick = () => {
    setIsDisplayVisible(!isDisplayVisible);
  };

  return (
    <div className={styles.container}>
      <div className={styles["sub-container"]}>
        <div className={styles["mobile-container"]}>
          <button
            onClick={handleMobileClick}
            className={
              isMobile
                ? styles["mobile-button-active"]
                : styles["mobile-button"]
            }
          >
            <FontAwesomeIcon
              icon={faMobileAlt}
              className={
                isMobile ? styles["mobile-icon-active"] : styles["mobile-icon"]
              }
              size="sm"
            />
          </button>
          <button
            onClick={handleDesktopClick}
            className={
              isMobile
                ? styles["mobile-button"]
                : styles["mobile-button-active"]
            }
          >
            <FontAwesomeIcon
              icon={faDesktop}
              className={
                isMobile ? styles["mobile-icon"] : styles["mobile-icon-active"]
              }
              size="1x"
            />
          </button>
        </div>

        <div className={styles["image-container"]}>
          <button
            onClick={handleImageButtonClick}
            className={
              isFeedItemVisible
                ? styles["image-button"]
                : styles["image-button-active"]
            }
          >
            <FontAwesomeIcon
              icon={faImage}
              className={
                isFeedItemVisible
                  ? styles["image-icon"]
                  : styles["image-icon-active"]
              }
              size="lg"
            />
          </button>

          <button
            onClick={handleTextButtonClick}
            className={
              isDisplayVisible
                ? styles["image-button"]
                : styles["image-button-active"]
            }
          >
            <FontAwesomeIcon
              icon={faTextHeight}
              className={
                isDisplayVisible
                  ? styles["image-icon"]
                  : styles["image-icon-active"]
              }
              // size="md"
              size="lg" // Updated to address invalid FontAwesome size
              // Change the above accordingly based on design needs (just not "md", as that's invalid)
            />
          </button>
        </div>
      </div>

      <div className={isMobile ? styles.mobile : styles.desktop}>
        {/* Updated to handle rendering with invalid/missing feedItem data */}
        {feedItem &&
        (feedItem.type === "Standard Content" ||
          feedItem.type === "Standing Order") ? (
          <>
            {isFeedItemVisible && (
              /*
              Update to exclude Banner since it doesn't interact with the modal
              Reference src/NewsAdmin/Shared/FeedBanner/FeedBanner.jsx
              */
              <section
                className={styles["feed-item"]}
                onClick={() => {
                  if (feedItem?.type !== "Banner") {
                    setIsModalOpen(!isModalOpen);
                  }
                }}
              >
                <FeedItem
                  feedItem={feedItem}
                  content={feedItem.content}
                  isPreview={false}
                />
              </section>
            )}

            {isDisplayVisible && (
              <section className={styles["display-container"]}>
                {/* Update to ensure this works with both live and mapped preview data */}
                {/* (prevents '.map()' crashes when previewing) */}
                <DisplaySectionContent
                  sectionElements={
                    feedItem?.data?.content?.sections ||
                    feedItem?.content?.sections ||
                    []
                  }
                  isHidden={false}
                />
              </section>
            )}
          </>
        ) : (
          <>
            <section className={styles["banner-item"]}>
              <BannerItem
                bannerSrc={feedItem?.information?.img || feedItem?.information?.image || feedItem?.imgSrc}
                align={feedItem?.information?.align ?? feedItem?.information?.position}
              />
            </section>
          </>
        )}
      </div>

      {/* Updated to handle rendering with invalid/missing feedItem data */}
      {feedItem &&
        (feedItem.type === "Standard Content" ||
          feedItem.type === "Standing Order") && (
          <CSSTransition
            nodeRef={modalRef} // StrictMode integrity
            classNames={{
              enter: styles["fade-enter"],
              exit: styles["fade-exit"],
              enterActive: styles["fade-enter-active"],
              exitActive: styles["fade-exit-active"],
            }}
            in={isModalOpen}
            timeout={300}
            unmountOnExit
          >
            <FeedItemModal
              ref={modalRef} // StrictMode integrity
              isOpen={isModalOpen}
              setIsOpen={() => setIsModalOpen(!isModalOpen)}
              feedItem={feedItem}
            />
          </CSSTransition>
        )}
    </div>
  );
}

FeedItemPreview.propTypes = {
  /**
   * String that states the type of feed template.
   */
  // feedItem: PropTypes.PropTypes.oneOfType([
  feedItem: PropTypes.oneOfType([
    // Corrected typo (double 'PropTypes')
    /**
     * Standard Content Object
     */
    PropTypes.shape({
      // type: "Standard Content",
      type: PropTypes.string.isRequired, // Correct type (not string literal)
      information: PropTypes.object,
      content: PropTypes.shape({
        sections: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.oneOf([
              "text",
              "image",
              "divider",
              "media",
              "cms",
              "bsm",
            ]),
            settings: PropTypes.object,
            text: PropTypes.string,
          })
        ),
      }),
    }),

    /**
     * Standing Order
     */
    PropTypes.shape({
      type: "Standing Order",
      options: PropTypes.object,
      information: PropTypes.object,
      content: PropTypes.arrayOf(
        PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.oneOf([
              "text",
              "image",
              "divider",
              "media",
              "cms",
              "bsm",
            ]),
          })
        )
      ),
    }),

    /**
     * Banner
     */
    PropTypes.shape({
      type: "Standard Content",
      information: PropTypes.object,
    }),
  ]),

  id: PropTypes.number,
  auId: PropTypes.number,
};
